// #region imports

// --- Angular
import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

// --- RxJs
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { JournalingBaseService } from './journaling-base.service';
import {
  ApplicationError,
  ErrorMessage,
  JournalEntryModel,
  JournalEntryTypes,
  PinnedJournalEntryTextModel,
  ToastEventModel,
  UnpinnedJournalModel,
  UpdateJournalEntryModel,
} from '../../../models';
import { environment } from '../../../../environments/environment';
import { AppService } from '../app.service';
import { QuadrantsBaseService } from './quadrants-base.service';
import { AppToastService } from '../app-toast.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class JournalingService extends JournalingBaseService {
  private _mostRecentlyDeletedJournalEntry: JournalEntryModel | null | undefined;
  private _mostRecentlyUpdatedJournalEntry: JournalEntryModel | null | undefined;

  constructor(
    private http: HttpClient,
    appService: AppService,
    translateService: TranslateService,
    quadrantsService: QuadrantsBaseService,
    toastService: AppToastService,
    router: Router,
  ) {
    super(appService, translateService, quadrantsService, toastService, router);
  }

  getJournalEntriesByQuadrant(quadrantId: string): void {
    this.appService.setLoading(true);

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.journalingApi +
      environment.apiConfig.journalingQuadrantApi;

    this.http
      .get<JournalEntryModel[]>(url, { params: { quadrantId } })
      .pipe(retryBackoff(environment.retryConfig))
      .subscribe((result) => {
        this._entriesByQuadrantId$.next(result);
        this.appService.setLoading(false);
      });
  }

  getAllJournalPins(): void {
    if (this._allJournalPins$.value) {
      return;
    }
    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.journalingApi +
      '/getAllJournalPins';
    this.http
      .get<PinnedJournalEntryTextModel[]>(url)
      .pipe(retryBackoff(environment.retryConfig))
      .subscribe((result) => {
        this._allJournalPins$.next(result);
      });
  }

  getPinnedJournalEntryTextByStyleId(styleId: string): void {
    this._selectedStyleId$.next(styleId);
    const textEntries = this._journalTextEntries$.value;
    if (textEntries.has(styleId)) {
      return;
    }
    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.journalingApi +
      '/getpinnedjournalentrytextbystyleid';

    this.http
      .get<PinnedJournalEntryTextModel[]>(url, { params: { styleId } })
      .pipe(retryBackoff(environment.retryConfig))
      .subscribe((result) => {
        textEntries.set(styleId, result);
        this._journalTextEntries$.next(textEntries);
      });
  }

  getJournalEntryById(journalEntryId: string): void {
    throw new Error('Method not implemented.');
  }

  createJournalEntry(entry: JournalEntryModel): void {
    if (this._rejectDuplicateEntry(entry)) {
      return;
    }
    this.appService.setLoading(true);

    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.journalingApi;

    combineLatest([
      this.http
        .post<JournalEntryModel>(url, entry)
        .pipe(
          retryBackoff(environment.retryConfig),
          catchError(this._createError.bind(this))
        ) as Observable<JournalEntryModel>,
      this.quadrantsService.quadrants$,
    ])
      .pipe(
        map(([apiResult, quadrants]) => ({
          apiResult,
          quadrants,
        }))
      )
      .subscribe((result) => {
        const newEntry = result.apiResult;
        if (!newEntry) {
          return;
        }
        const quadrants = result.quadrants;
        if (!quadrants) {
          return;
        }
        this.appService.setLoading(false);

        const quadrant = quadrants.find(
          (q) =>
            q.categoryModels.findIndex((c) => c.id === newEntry.styleId) >= 0
        );
        const quadrantId = quadrant && quadrant.id ? quadrant.id : 1;
        this._journalCreated$.next(quadrantId.toString());

        this._addEntryToJournalEntriesList(newEntry);
      });
  }

  pinJournalEntryText(entry: JournalEntryModel): void {
    this.appService.setLoading(true);

    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.journalingApi;

    combineLatest([
      this.http
        .post<JournalEntryModel>(url, entry)
        .pipe(
          retryBackoff(environment.retryConfig),
          catchError(this._createError.bind(this))
        ) as Observable<JournalEntryModel>,
      this.quadrantsService.quadrants$,
    ])
      .pipe(
        map(([apiResult, quadrants]) => ({
          apiResult,
          quadrants,
        }))
      )
      .subscribe((result) => {
        const pinnedEntry = result.apiResult;
        if (!pinnedEntry) {
          return;
        }
        const quadrants = result.quadrants;
        if (!quadrants) {
          return;
        }

        const quadrant = quadrants.find(
          (q) =>
            q.categoryModels.findIndex((c) => c.id === pinnedEntry.styleId) >= 0
        );
        const quadrantId = quadrant && quadrant.id ? quadrant.id : 1;
        this.appService.setLoading(false);
        this._journalPinned$.next(quadrantId.toString());
        this._toggleEntryText(result.apiResult, true);

        this._addEntryToJournalEntriesList(pinnedEntry);
      });
  }

  private _addEntryToJournalEntriesList(entry: JournalEntryModel) {
    combineLatest([
      this.quadrantsService.quadrants$,
      this.quadrantsService.currentId$,
    ])
      .pipe(
        map(([quadrants, currentQuadrant]) => ({
          quadrants,
          currentQuadrant,
        })),
        take(1)
      )
      .subscribe((quadrantInfo) => {
        if (!quadrantInfo.quadrants || !quadrantInfo.currentQuadrant) {
          return;
        }
        let addedToCurrentQuadrant = false;
        for (const quadrant of quadrantInfo.quadrants) {
          for (const category of quadrant.categoryModels) {
            if (category.id === entry.styleId) {
              addedToCurrentQuadrant =
                quadrant.id === quadrantInfo.currentQuadrant;
              break;
            }
          }
        }
        if (addedToCurrentQuadrant) {
          let allEntries: JournalEntryModel[] = [];
          if (
            this._entriesByQuadrantId$.value &&
            this._entriesByQuadrantId$.value.length > 0
          ) {
            allEntries = this._entriesByQuadrantId$.value.slice(0);
          }
          allEntries.unshift(entry);
          this._entriesByQuadrantId$.next(allEntries);
        }
      });
  }

  updateJournalEntryById(
    journalEntryId: string,
    entry: JournalEntryModel,
    canUndo = true
  ): void {
    if (
      !journalEntryId ||
      !entry ||
      entry.entryType !== JournalEntryTypes.Unpinned
    ) {
      return;
    }

    let undoUpdateItem: JournalEntryModel | null = null;
    if (
      canUndo &&
      this._entriesByQuadrantId$ &&
      this._entriesByQuadrantId$.value
    ) {
      const itemToUpdate = this._entriesByQuadrantId$.value.find(
        (j) => j.id === journalEntryId
      );
      if (itemToUpdate) {
        undoUpdateItem = {
          id: itemToUpdate.id,
          styleId: itemToUpdate.styleId,
          entryText: itemToUpdate.entryText,
          isImportant: itemToUpdate.isImportant,
          entryType: JournalEntryTypes.Unpinned,
        } as JournalEntryModel;
      }
    }
    this.appService.setLoading(true);
    const url = `${environment.apiConfig.surveyApiUrl}${environment.apiConfig.journalingApi}/${journalEntryId}`;
    this.http
      .put<UpdateJournalEntryModel>(url, {
        id: entry.id,
        styleId: entry.styleId,
        entryText: entry.entryText,
        isImportant: entry.isImportant,
      })
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._updateError.bind(this))
      )
      .subscribe((result) => {
        this.appService.setLoading(false);
        if (!result || !undoUpdateItem) {
          return;
        }
        this._mostRecentlyUpdatedJournalEntry = undoUpdateItem;
        this._journalEntryUpdated$.next(canUndo);
        const entries =
          this._entriesByQuadrantId$ && this._entriesByQuadrantId$.value;
        if (entries) {
          const updatedEntry = entries.find((je) => je.id === journalEntryId);
          if (updatedEntry) {
            updatedEntry.entryText = entry.entryText;
            updatedEntry.isImportant = entry.isImportant;
            updatedEntry.styleId = entry.styleId;
            this._entriesByQuadrantId$.next(entries);
          }
        }
      });
  }

  unpinJournalEntryText(entry: JournalEntryModel): void {
    if (!entry) {
      return;
    }

    const deletedItem = {
      entryText: null,
      entryType: JournalEntryTypes.Pinned,
      styleId: entry.styleId,
      id: entry.id,
      pinLocation: entry.pinLocation || entry.id,
      resourceKey: entry.resourceKey,
      isImportant: true,
    } as JournalEntryModel;

    const journalId = entry?.id ?? '';
    this._deleteJournalEntry(journalId, deletedItem);
  }

  deleteJournalEntry(journalEntry: UnpinnedJournalModel): void {
    if (!journalEntry) {
      return;
    }

    const deletedItem = {
      entryText: journalEntry.entryText,
      entryType: JournalEntryTypes.Unpinned,
      styleId: journalEntry.styleId,
      id: journalEntry.id,
      pinLocation: null,
      resourceKey: null,
      isImportant: journalEntry.isImportant,
    } as JournalEntryModel;

    const journalId = journalEntry.id;
    this._deleteJournalEntry(journalId, deletedItem);
  }

  private _deleteJournalEntry(
    journalId: string,
    deletedItem: JournalEntryModel
  ): void {
    this.appService.setLoading(true);
    const url = `${environment.apiConfig.surveyApiUrl}${environment.apiConfig.journalingApi}/${journalId}`;
    this.http
      .delete(url, {
        responseType: 'text',
      })
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._deleteError.bind(this))
      )
      .subscribe((result) => {
        this.appService.setLoading(false);

        if (result === 'failed') {
          return;
        }
        this._mostRecentlyDeletedJournalEntry = deletedItem;
        const entries = this._entriesByQuadrantId$.value;
        if (entries) {
          const removeIndex = entries.findIndex((i) => i.id === journalId);
          entries.splice(removeIndex, 1);
          this._entriesByQuadrantId$.next(entries);
        }
        if (deletedItem.entryType === JournalEntryTypes.Pinned) {
          this._journalUnpinned$.next(result);
          this._toggleEntryText(deletedItem, false);
        } else {
          this._journalEntryDeleted$.next(journalId);
        }
      });
  }

  undelete(): void {
    if (this._mostRecentlyDeletedJournalEntry) {
      const undeletedEntry = this._mostRecentlyDeletedJournalEntry;
      this._mostRecentlyDeletedJournalEntry = null;
      if (undeletedEntry.entryType === JournalEntryTypes.Pinned) {
        this.pinJournalEntryText(undeletedEntry);
      } else {
        this.createJournalEntry(undeletedEntry);
      }
    }
  }

  revert(): void {
    if (this._mostRecentlyUpdatedJournalEntry && this._mostRecentlyUpdatedJournalEntry.id) {
      const revertedEntry = this._mostRecentlyUpdatedJournalEntry;
      this._mostRecentlyUpdatedJournalEntry = null;
      this.updateJournalEntryById(revertedEntry.id!, revertedEntry, false);
    }
  }

  private _rejectDuplicateEntry(entry: JournalEntryModel): boolean {
    if (
      !this._entriesByQuadrantId$ ||
      !this._entriesByQuadrantId$.value ||
      this._entriesByQuadrantId$.value.length === 0
    ) {
      return false;
    }
    if (entry.entryType === JournalEntryTypes.Pinned) {
      const existingPin = this._entriesByQuadrantId$.value.findIndex(
        (je) => je.pinLocation === entry.pinLocation
      );
      return existingPin >= 0;
    }

    const existingJe = this._entriesByQuadrantId$.value.findIndex(
      (je) => je.id === entry.id
    );
    return existingJe >= 0;
  }

  protected _createError(err: any): Observable<JournalEntryModel | null> {

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'JournalingService',
      false, err
    );

    this.appService.setLastError(error);
    return of(null);
  }

  private _updateError(err: any): Observable<UpdateJournalEntryModel> {

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'JournalingService',
      false,
      err
    );

    this.appService.setLastError(error);
    return of({} as UpdateJournalEntryModel);
  }

  private _deleteError(err: any) {

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'JournalingService',
      false,
      err
    );

    this.appService.setLastError(error);
    this._mostRecentlyDeletedJournalEntry = null;
    return of('failed');
  }
}
